/**
 * Meal Plan API Service
 *
 * With Better Auth, session cookies are sent automatically via credentials: 'include'.
 */

import { ApiError, apiFetch } from "./client";
import { clientLocalDateHeader } from "../dates";
import { recommendationRequestHeaders, recommendationAcceptanceHeaders, type RecommendationRequestMetadata, type RecommendationAcceptanceMetadata } from "../recommendation-attribution";
import type {
  MealPlan,
  CreateMealPlanRequest,
  SwapMealRequest,
  AddMealRequest,
  MealSlot,
  MealType,
  Recipe,
  SuggestMealRequest,
  SuggestMealResponse,
  PreviewSuggestedMealRequest,
  PreviewSuggestedMealResponse,
  PreviewMealPlanRequest,
  CommitMealPlanRequest,
  CommitMealPlanResponse,
  MealPlanPreview,
  CandidateMenuErrorDetails,
} from "./types";

const CANDIDATE_ERROR_CODES = new Set<CandidateMenuErrorDetails["code"]>([
  "NO_SELECTED_RECIPES",
  "OPEN_SLOTS_ACK_REQUIRED",
  "REPLACE_CONFIRMATION_REQUIRED",
  "PREVIEW_STALE",
  "PLAN_CHANGED",
  "WEEK_ALREADY_PLANNED",
  "MULTIPLE_PLANS_FOR_WEEK",
  "COMMIT_ID_REUSED",
  "RESULT_DELETED",
  "NO_ELIGIBLE_CANDIDATES",
  "COMMIT_FAILED",
  "COMMIT_TIMED_OUT",
]);
const CANDIDATE_MEAL_TYPES = new Set<MealType>([
  "breakfast",
  "lunch",
  "dinner",
  "snack",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMealTypeList(value: unknown): value is MealType[] {
  return (
    Array.isArray(value) &&
    value.every(
      (mealType) =>
        typeof mealType === "string" &&
        CANDIDATE_MEAL_TYPES.has(mealType as MealType),
    )
  );
}

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isOpenCounts(
  value: unknown,
): value is NonNullable<CandidateMenuErrorDetails["openCounts"]> {
  if (!isRecord(value)) return false;
  return [...CANDIDATE_MEAL_TYPES].every(
    (mealType) =>
      Number.isInteger(value[mealType]) &&
      (value[mealType] as number) >= 0,
  );
}

/**
 * Rebuild the privacy-safe candidate error DTO instead of leaking arbitrary
 * fields from an API error body into UI state or analytics.
 */
export function parseCandidateMenuError(
  error: unknown,
): CandidateMenuErrorDetails | null {
  if (!(error instanceof ApiError) || !isRecord(error.data)) return null;
  const details = error.data.details;
  if (!isRecord(details) || typeof details.code !== "string") return null;
  if (!CANDIDATE_ERROR_CODES.has(details.code as CandidateMenuErrorDetails["code"])) {
    return null;
  }
  if (details.mealTypes !== undefined && !isMealTypeList(details.mealTypes)) {
    return null;
  }
  if (
    details.invalidRecipeIds !== undefined &&
    !isStringList(details.invalidRecipeIds)
  ) {
    return null;
  }
  if (details.openCounts !== undefined && !isOpenCounts(details.openCounts)) {
    return null;
  }

  return {
    code: details.code as CandidateMenuErrorDetails["code"],
    ...(details.mealTypes === undefined
      ? {}
      : { mealTypes: details.mealTypes }),
    ...(details.invalidRecipeIds === undefined
      ? {}
      : { invalidRecipeIds: details.invalidRecipeIds }),
    ...(details.openCounts === undefined
      ? {}
      : {
          openCounts: {
            breakfast: details.openCounts.breakfast,
            lunch: details.openCounts.lunch,
            dinner: details.openCounts.dinner,
            snack: details.openCounts.snack,
          },
        }),
  };
}

/** Read-only candidate computation; browser caching would make Review stale. */
export async function previewMealPlan(
  request: PreviewMealPlanRequest,
  signal?: AbortSignal,
  attribution?: RecommendationRequestMetadata,
): Promise<MealPlanPreview> {
  return apiFetch<MealPlanPreview>("/meal-plans/preview", {
    method: "POST",
    body: JSON.stringify(request),
    cache: "no-store",
    signal,
    ...(attribution ? { headers: recommendationRequestHeaders(attribution) } : {}),
  });
}

/** Idempotent candidate selection commit. The caller owns and retains UUID. */
export async function commitMealPlan(
  request: CommitMealPlanRequest,
  attribution?: RecommendationAcceptanceMetadata,
  expectedActorUserId?: string,
): Promise<CommitMealPlanResponse> {
  return apiFetch<CommitMealPlanResponse>("/meal-plans/commit", {
    method: "POST",
    body: JSON.stringify(request),
    // #1138: the API decides active-vs-draft from the caller's current week.
    headers: {
      ...clientLocalDateHeader(),
      ...(attribution ? recommendationAcceptanceHeaders(attribution) : {}),
      ...(expectedActorUserId ? { "X-Expected-User-Id": expectedActorUserId } : {}),
    },
  });
}

/**
 * Body of PUT /meal-plans/:id/slots/:slotId/reschedule (ladder item 12).
 * `date` is a calendar date ("YYYY-MM-DD" -- use formatLocalDate, never an
 * ISO instant); `mealType` omitted keeps the slot's own mealType.
 */
export interface RescheduleMealRequest {
  date: string;
  mealType?: MealType;
}

/**
 * Reschedule response: `slot` is the moved slot at its new position;
 * `swappedWith` is the slot that previously occupied the target position
 * (the two traded places), or null when the target was empty (plain move).
 * The distinction drives the "Moved to Friday" vs "Swapped with Friday's
 * dinner" toast.
 */
export interface RescheduleMealResponse {
  slot: MealSlot;
  swappedWith: MealSlot | null;
}

/**
 * Generate a new meal plan
 */
export async function generateMealPlan(
  request: CreateMealPlanRequest,
): Promise<MealPlan> {
  return apiFetch<MealPlan>("/meal-plans/generate", {
    method: "POST",
    body: JSON.stringify(request),
    headers: clientLocalDateHeader(),
  });
}

/**
 * Get all meal plans for the current user
 */
export async function getMealPlans(): Promise<MealPlan[]> {
  return apiFetch<MealPlan[]>("/meal-plans");
}

/**
 * Get a specific meal plan by ID
 */
export async function getMealPlan(id: string): Promise<MealPlan> {
  return apiFetch<MealPlan>(`/meal-plans/${id}`);
}

/**
 * Swap a meal in a meal plan
 */
export async function swapMeal(
  mealPlanId: string,
  request: SwapMealRequest,
  attribution?: RecommendationAcceptanceMetadata,
  expectedActorUserId?: string,
  practicalFitRevision?: string,
): Promise<MealSlot> {
  return apiFetch<MealSlot>(`/meal-plans/${mealPlanId}/swap-meal`, {
    method: "PUT",
    body: JSON.stringify(request),
    ...(attribution || expectedActorUserId || practicalFitRevision ? { headers: {
      ...(practicalFitRevision ? { "X-Practical-Fit-Revision": practicalFitRevision } : {}),
      ...(attribution ? recommendationAcceptanceHeaders(attribution) : {}),
      ...(expectedActorUserId ? { "X-Expected-User-Id": expectedActorUserId } : {}),
    } } : {}),
  });
}

/**
 * Add a new meal to a meal plan
 */
export async function addMeal(
  mealPlanId: string,
  request: AddMealRequest,
): Promise<MealSlot> {
  return apiFetch<MealSlot>(`/meal-plans/${mealPlanId}/add-meal`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Ask the server to score and add one recipe to an exact empty slot.
 * `cuisine`, when present, is scoped to this request and never persisted as
 * a preference.
 */
export async function suggestMeal(
  mealPlanId: string,
  request: SuggestMealRequest,
): Promise<SuggestMealResponse> {
  return apiFetch<SuggestMealResponse>(`/meal-plans/${mealPlanId}/suggestions`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** Score an empty slot without changing the plan, groceries or preferences. */
export function previewSuggestedMeal(
  mealPlanId: string,
  request: PreviewSuggestedMealRequest,
  attribution?: RecommendationRequestMetadata,
  expectedActorUserId?: string,
): Promise<PreviewSuggestedMealResponse> {
  return apiFetch(`/meal-plans/${mealPlanId}/suggestions/preview`, {
    method: "POST", body: JSON.stringify(request),
    ...(attribution || expectedActorUserId ? { headers: {
      ...(attribution ? recommendationRequestHeaders(attribution) : {}),
      ...(expectedActorUserId ? { "X-Expected-User-Id": expectedActorUserId } : {}),
    } } : {}),
  });
}

/**
 * Reschedule a meal (slot) to another day -- and optionally another meal
 * type -- within the same plan. Preserves the plan AND its grocery list
 * (the server deliberately never refreshes the list: same recipes, same
 * servings, only dates change).
 */
export async function rescheduleMeal(
  mealPlanId: string,
  slotId: string,
  request: RescheduleMealRequest,
): Promise<RescheduleMealResponse> {
  return apiFetch<RescheduleMealResponse>(
    `/meal-plans/${mealPlanId}/slots/${slotId}/reschedule`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    },
  );
}

/**
 * Change how many servings a planned meal makes (issue #1201). Plain recipe
 * slots only -- the API answers 409 `batch_linked` for a slot that cooks or
 * draws from a leftovers batch.
 */
export async function updateMealServings(
  mealPlanId: string,
  slotId: string,
  servings: number,
): Promise<MealSlot> {
  return apiFetch<MealSlot>(
    `/meal-plans/${mealPlanId}/slots/${slotId}/servings`,
    {
      method: "PUT",
      body: JSON.stringify({ servings }),
    },
  );
}

/**
 * Remove a single meal (slot) from a meal plan.
 */
export async function removeMeal(
  mealPlanId: string,
  slotId: string,
): Promise<void> {
  return apiFetch<void>(`/meal-plans/${mealPlanId}/slots/${slotId}`, {
    method: "DELETE",
  });
}

/**
 * Delete a meal plan
 */
export async function deleteMealPlan(id: string): Promise<void> {
  return apiFetch<void>(`/meal-plans/${id}`, {
    method: "DELETE",
  });
}

/**
 * Reset a meal plan in place (Track C, Task 5, issue #448): wipes the
 * plan's slots and regenerates them into the SAME plan row -- the plan id
 * (and its grocery list's id, refreshed alongside it server-side) never
 * change, unlike a delete + generate.
 */
export async function resetMealPlan(id: string): Promise<MealPlan> {
  return apiFetch<MealPlan>(`/meal-plans/${id}/reset`, {
    method: "POST",
  });
}

/**
 * Get swap suggestions for a meal slot
 */
export async function getSwapSuggestions(
  mealSlotId: string,
  count: number = 5,
  attribution?: RecommendationRequestMetadata,
  signal?: AbortSignal,
): Promise<Recipe[] | { recipes: Recipe[]; attribution?: import("@batch-and-bowl/shared").RecommendationAttributionReceipt }> {
  return apiFetch(
    `/meal-plans/swap-suggestions/${mealSlotId}?count=${count}`,
    ...(attribution ? [{ headers: recommendationRequestHeaders(attribution), signal, cache: "no-store" as const }] : []),
  );
}
